import type { Directive } from 'vue';

/**
 * Sandboxed module documents (§6.4, §7.3): `<iframe sandbox="allow-scripts">` on the sandbox page, without
 * same-origin, a restrictive CSP (no network) and a small postMessage bridge `window.vp`.
 *
 * The bridge of a view or panel (API 2.0):
 * - `vp.post(data)` / `vp.on(fn)` — messages with the module (`{vpView:1, data}` on the wire);
 * - `vp.reserve({ top, width })` — room for the view's toolbar outside its object (screen px);
 * - elements marked `data-vp-popup` (open menus) get room below the object automatically;
 * - `vp.viewport` / `vp.onViewport(fn)` — { zoom, space, side, padX, below } the platform gave;
 * - `vp.fit()` — measure the content again now (the bridge does it by itself on changes);
 * - `vp.close()` — a module window (API 2.3) asks to be closed.
 * Room, viewport and content size travel on the platform's own channel (`{vpBridge:1, …}`), which a
 * module cannot imitate through `vp.post`.
 * API 4.6: the bridge reports Escape (the platform takes the keyboard back) and every request the
 * document's policy refused (`csp`: a violation of the module); its listeners come before the
 * module's scripts, so a module cannot stop them.
 */
const BRIDGE = `<script>(function(){var P=parent,L=[],V=[],vw={zoom:1,space:0,side:'top',padX:0,below:0},res={top:0,width:0},last='',lastSize='',raf=0;
function sys(m){m.vpBridge=1;P.postMessage(m,'*')}
function popupBelow(){if(!res.top)return 0;var els=document.querySelectorAll('[data-vp-popup]'),m=0;for(var i=0;i<els.length;i++){var r=els[i].getBoundingClientRect();if(r.width||r.height)m=Math.max(m,r.bottom)}var base=innerHeight-vw.below/vw.zoom;return m>base?Math.ceil((m-base)*vw.zoom)+8:0}
function extent(){var b=document.body;if(!b)return null;var cs=getComputedStyle(b),w=0,h=0,c=b.querySelectorAll('body > *');for(var i=0;i<c.length;i++){var e=c[i];if(e.hasAttribute('data-vp-popup')||e.hasAttribute('data-vp-chrome'))continue;var st=getComputedStyle(e);if(st.position==='fixed'||st.display==='none')continue;var r=e.getBoundingClientRect();if(!r.width&&!r.height)continue;w=Math.max(w,r.right+(parseFloat(st.marginRight)||0));h=Math.max(h,r.bottom+(parseFloat(st.marginBottom)||0))}w+=(parseFloat(cs.paddingRight)||0)+(parseFloat(cs.marginRight)||0)+scrollX;h+=(parseFloat(cs.paddingBottom)||0)+(parseFloat(cs.marginBottom)||0)+scrollY;var top=vw.side==='top'?vw.space/vw.zoom:0;return{w:Math.ceil(w-2*vw.padX/vw.zoom),h:Math.ceil(h-top)}}
function send(){var b=popupBelow(),k=res.top+':'+res.width+':'+b;if(k!==last){last=k;sys({chrome:{top:res.top,width:res.width,below:b}})}var e=extent();if(e){var s=e.w+'x'+e.h;if(s!==lastSize){lastSize=s;sys({size:e})}}}
function later(){if(!raf)raf=requestAnimationFrame(function(){raf=0;send()})}
window.vp={post:function(d){P.postMessage({vpView:1,data:d},'*')},on:function(f){L.push(f)},reserve:function(o){o=o||{};res={top:Math.max(0,+o.top||0),width:Math.max(0,+o.width||0)};send()},get viewport(){return vw},onViewport:function(f){V.push(f)},fit:function(){lastSize='';send()},close:function(){sys({close:true})}};
addEventListener('message',function(e){if(e.source!==P||!e.data)return;var i;if(e.data.vpBridge){var v=e.data.viewport;if(v){vw={zoom:v.zoom>0?v.zoom:1,space:v.space||0,side:v.side==='bottom'?'bottom':'top',padX:v.padX||0,below:v.below||0};for(i=0;i<V.length;i++){try{V[i](vw)}catch(x){}}later()}return}if(!e.data.vpView)return;var d=e.data.data;if(d&&d.type==='deactivated'){res={top:0,width:0};later()}for(i=0;i<L.length;i++){try{L[i](d)}catch(x){}}});
new MutationObserver(later).observe(document,{subtree:true,childList:true,characterData:true,attributes:true,attributeFilter:['class','style','hidden','open','data-vp-popup']});
addEventListener('keydown',function(e){if(e.key==='Escape')sys({escape:true})},true);
document.addEventListener('securitypolicyviolation',function(e){sys({csp:String(e.effectiveDirective||e.violatedDirective||'').slice(0,40)})},true);
addEventListener('resize',later);addEventListener('load',later);if(typeof ResizeObserver==='function')addEventListener('DOMContentLoaded',function(){new ResizeObserver(later).observe(document.body)});later();})();</script>`;
const CSP = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; connect-src 'none'; form-action 'none'">`;

export function buildSrcdoc(html: string): string {
  const head = CSP + BRIDGE;
  if (/<head[^>]*>/i.test(html)) return html.replace(/<head[^>]*>/i, (m) => m + head);
  if (/<html[^>]*>/i.test(html)) return html.replace(/<html[^>]*>/i, (m) => m + '<head>' + head + '</head>');
  return '<!doctype html><html><head>' + head + '</head><body>' + html + '</body></html>';
}

export const SANDBOX = 'allow-scripts';

/**
 * Features of the browser a sandboxed document never gets (API 4.6): the iframe's `allow` list denies
 * them explicitly. Full screen is given only to modules with the `fullscreen` permission.
 */
const DENIED_FEATURES = ['camera', 'microphone', 'geolocation', 'display-capture', 'usb', 'serial', 'hid', 'bluetooth', 'midi', 'payment', 'accelerometer', 'gyroscope', 'magnetometer', 'clipboard-read', 'clipboard-write', 'screen-wake-lock', 'web-share'];

/** A module document and what its sandbox allows beyond scripts (API 4.6: `fullscreen`, `pointer-lock`). */
export interface SandboxDoc {
  html: string | null | undefined;
  fullscreen?: boolean;
  pointerLock?: boolean;
}

function normalize(v: string | SandboxDoc | null | undefined): SandboxDoc {
  return typeof v === 'object' && v !== null ? v : { html: v };
}

/** The `sandbox` and `allow` attributes for a document. */
export function sandboxAttrs(d: SandboxDoc): { sandbox: string; allow: string } {
  return {
    sandbox: SANDBOX + (d.pointerLock ? ' allow-pointer-lock' : ''),
    allow: [...DENIED_FEATURES.map((f) => `${f} 'none'`), d.fullscreen ? 'fullscreen *' : "fullscreen 'none'"].join('; '),
  };
}

/**
 * The page module documents are shown in (public/sandbox.html): a real address with its own
 * Content-Security-Policy. A `srcdoc` frame would inherit the app's policy, which then would have to
 * allow inline scripts for the whole app.
 */
export const SANDBOX_PAGE = `${import.meta.env.BASE_URL}sandbox.html`;

const frames = new WeakMap<HTMLIFrameElement, { html: string; fed: boolean; acked?: boolean; key: string }>();
let loads = 0;

function feed(el: HTMLIFrameElement): void {
  const d = frames.get(el);
  // once per load: the page takes only the first document it is given
  if (!d || d.fed) return;
  d.fed = true;
  el.contentWindow?.postMessage({ vpSandbox: 1, html: d.html }, '*');
}

type SandboxReporter = (moduleId: string, kind: 'unsafe' | 'csp', detail: string) => void;
let reporter: SandboxReporter | null = null;
/**
 * API 4.6: what module documents report about themselves — a sandbox page that found itself not
 * isolated, a request the document's policy refused. The iframe names its module (`data-module`).
 */
export function onSandboxReport(fn: SandboxReporter): void {
  reporter = fn;
}

/**
 * The sandbox page answers once it has checked its isolation (API 4.6) and written the document (or
 * refused to): messages posted from then on reach the module's document. Refusals of its policy come
 * on the platform's channel of the bridge.
 */
addEventListener('message', (e) => {
  const m = e.data as { vpSandbox?: unknown; written?: unknown } | null;
  const ack = !!m && m.vpSandbox === 1 && typeof m.written === 'boolean';
  const bridge = ack ? null : readBridgeMessage(e.data);
  if (!ack && !bridge?.csp) return;
  for (const el of document.querySelectorAll('iframe[sandbox]')) {
    const f = el as HTMLIFrameElement;
    if (f.contentWindow !== e.source) continue;
    const moduleId = f.dataset['module'];
    if (bridge?.csp) {
      if (moduleId) reporter?.(moduleId, 'csp', bridge.csp);
      return;
    }
    const d = frames.get(f);
    // once per load: a module document cannot announce itself again
    if (!d || !d.fed || d.acked) return;
    d.acked = true;
    if (m!.written) f.dispatchEvent(new CustomEvent(SANDBOX_FED));
    else if (moduleId) reporter?.(moduleId, 'unsafe', '');
    return;
  }
});

/** Fired on the iframe once its document was handed to the sandbox page. */
export const SANDBOX_FED = 'vp-sandbox-fed';

function show(el: HTMLIFrameElement, doc: SandboxDoc): void {
  const html = doc.html;
  if (!html) {
    frames.delete(el);
    el.removeAttribute('src');
    return;
  }
  // the sandbox flags apply on the next load: set them before the address changes
  const a = sandboxAttrs(doc);
  el.setAttribute('sandbox', a.sandbox);
  el.setAttribute('allow', a.allow);
  frames.set(el, { html, fed: false, key: a.sandbox + '|' + a.allow });
  // a new address loads the page afresh (the old document is replaced, not patched)
  el.src = `${SANDBOX_PAGE}?d=${++loads}`;
}

/**
 * `<iframe v-sandbox="html">` or `v-sandbox="{ html, fullscreen, pointerLock }"`: the module document
 * in the sandbox page; a new document or other sandbox flags reload it.
 */
export const vSandbox: Directive<HTMLIFrameElement, string | SandboxDoc | null | undefined> = {
  mounted(el, binding) {
    el.setAttribute('sandbox', SANDBOX);
    el.setAttribute('referrerpolicy', 'no-referrer');
    el.addEventListener('load', () => feed(el));
    show(el, normalize(binding.value));
  },
  updated(el, binding) {
    const now = normalize(binding.value);
    const d = frames.get(el);
    const a = sandboxAttrs(now);
    if (now.html !== d?.html || a.sandbox + '|' + a.allow !== d?.key) show(el, now);
  },
};

export function messageSize(data: unknown): number {
  try {
    return JSON.stringify(data)?.length ?? 0;
  } catch {
    return Infinity;
  }
}

/** A message on the platform's own channel from a sandboxed document: room for its toolbar, content size. */
export interface BridgeMessage {
  chrome?: { top: number; width: number; below: number };
  size?: { w: number; h: number };
  /** A module window asks to be closed. */
  close?: boolean;
  /** API 4.6: Escape was pressed in the document: the platform takes the keyboard back. */
  escape?: boolean;
  /** API 4.6: the document's policy refused something (the directive): a violation of the module. */
  csp?: string;
}

export function readBridgeMessage(data: unknown): BridgeMessage | null {
  if (!data || typeof data !== 'object' || (data as { vpBridge?: unknown }).vpBridge !== 1) return null;
  const out: BridgeMessage = {};
  const c = (data as { chrome?: Record<string, unknown> }).chrome;
  if (c) {
    const top = Number(c['top']);
    const width = Number(c['width']);
    const below = Number(c['below']);
    if (Number.isFinite(top) && top >= 0 && top <= 400)
      out.chrome = { top: Math.round(top), width: Number.isFinite(width) && width > 0 && width <= 1600 ? Math.round(width) : 0, below: Number.isFinite(below) && below > 0 && below <= 600 ? Math.round(below) : 0 };
  }
  const s = (data as { size?: Record<string, unknown> }).size;
  if (s) {
    const w = Number(s['w']);
    const h = Number(s['h']);
    if (Number.isFinite(w) && Number.isFinite(h) && w >= 0 && h >= 0 && w <= 100000 && h <= 100000) out.size = { w, h };
  }
  if ((data as { close?: unknown }).close === true) out.close = true;
  if ((data as { escape?: unknown }).escape === true) out.escape = true;
  const csp = (data as { csp?: unknown }).csp;
  if (typeof csp === 'string' && /^[a-z-]{0,40}$/.test(csp)) out.csp = csp || 'policy';
  return out;
}
